import { inject, Injectable, InjectionToken } from '@angular/core';
import { BAZIS_API_V, BazisSrvService } from '@bazis/shared/services/srv.service';
import {
    BehaviorSubject,
    combineLatest,
    merge,
    Observable,
    of,
    shareReplay,
    switchMap,
    withLatestFrom,
} from 'rxjs';
import { EntData, EntList, GroupRequestItem } from '@bazis/shared/models/srv.types';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { BazisWebSocketService } from '@bazis/shared/services/web-socket.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { buildFilterStr } from '@bazis/utils';
import { ListPagination } from '@bazis/list/models/list.types';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { debounceTime, filter, map, take, tap } from 'rxjs/operators';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { BazisToastService } from '@bazis/shared/services/toast.service';

/**
 * Tab-specific filter params applied to notifications list.
 * Used together with main `filter$` and websocket channel filter.
 */
const TAB_PARAMS = {
    all: {},
    emergency: {
        template__notice_level: 'danger',
    },
    important: {
        is_important: 'true',
    },
    informing: {
        template__notice_level: 'info',
    },
};

/**
 * Static tab definitions for notifications UI.
 * Counts are filled dynamically by `tabList$`.
 */
const ALL_TABS = [
    {
        id: 'all',
        titleKey: 'listStaticFilter.all',
        titleParams: { count: 0 },
    },
    // {
    //     id: 'emergency',
    //     titleKey: 'tab.emergency',
    //     titleParams: { count: 0 },
    // },
    {
        id: 'important',
        titleKey: 'tab.important',
        titleParams: { count: 0 },
    },
    // {
    //     id: 'informing',
    //     titleKey: 'tab.informing',
    //     titleParams: { count: 0 },
    // },
];

/**
 * Injection token for roles that should not receive notifications.
 *
 * When current role is present in this list, `unreadNotifications$` returns `null`
 * and does not request unread notifications from backend.
 *
 * Default value is an empty array.
 *
 * Provide it in app/module providers if you need to disable notifications for some roles:
 *
 * ```ts
 * providers: [
 *   { provide: DISABLE_NOTIFICATIONS_ROLES_DATA, useValue: ['role_slug_1', 'role_slug_2'] },
 * ]
 * ```
 */
export const DISABLE_NOTIFICATIONS_ROLES_DATA = new InjectionToken<string[]>('', {
    providedIn: 'root',
    factory: () => [],
});

/**
 * **BazisNotificationService**
 *
 * Notification center service.
 * Combines websocket notifications with backend lists, keeps a local storage map of notifications,
 * supports pagination and tab filters, and provides actions to mark notifications read/unread
 * and toggle importance.
 *
 * Also converts websocket “toast” notifications into UI toasts via `BazisToastService`.
 */
@Injectable({ providedIn: 'root' })
export class BazisNotificationService {
    protected srv = inject(BazisSrvService);

    protected socketService = inject(BazisWebSocketService);

    protected entityService = inject(BazisEntityService);

    protected authService = inject(BazisAuthService);

    protected toastService = inject(BazisToastService);

    public disableNotificationsRoles: string[] = inject(DISABLE_NOTIFICATIONS_ROLES_DATA);

    public bazisApiV = inject(BAZIS_API_V);

    /** Loading indicator holder used by UI while list requests are in progress. */
    isLoading = new TemplateObservable(true);

    /** Trigger to refresh unread/top notifications list. */
    protected _updateTopNotificationsList$ = new BehaviorSubject(true);

    /** Trigger to refresh counts for tabs (`tabList$`). */
    protected _updateTabsCount$ = new BehaviorSubject(true);

    /**
     * Backend field name for websocket channel/provider, depends on API version.
     * - v2: `provider`
     * - other: `channel`
     */
    protected channelField = this.bazisApiV === '2' ? 'provider' : 'channel';

    /**
     * Backend “sent” state value, depends on API version.
     * - v2: `sent`
     * - other: `send`
     */
    protected sentState = this.bazisApiV === '2' ? 'sent' : 'send';

    /**
     * Pagination state for notifications list.
     * `offset` is derived from `nextPage$` and `limit`.
     */
    pagination: TemplateObservable<ListPagination> = new TemplateObservable({
        offset: 0,
        limit: 20,
        count: 0,
    });

    /**
     * Create the service.
     *
     * Important: constructor sets up a subscription that clears `_newNotifications`
     * when user id or role changes.
     *
     * Injection tokens:
     * - `DISABLE_NOTIFICATIONS_ROLES_DATA`: list of role slugs for which unread notifications are disabled
     * - `BAZIS_API_V`: API version string that affects request field names and state values
     *
     * @param srv - API service used for changing notification state and saving importance.
     * @param socketService - Websocket service that provides incoming notifications.
     * @param entityService - Entity service used to load notifications lists and counts.
     * @param authService - Auth service used for user and role context.
     * @param toastService - Toast service used to show websocket toast notifications.
     * @param disableNotificationsRoles - Injected list of role slugs to disable notifications for.
     * @param bazisApiV - Injected API version (example: `'2'`).
     */
    constructor() {
        combineLatest([this.authService.userId$, this.authService.role$])
            .pipe(
                debounceTime(100),
                tap((v) => {
                    this._newNotifications.set([]);
                    console.log('clr new notifications');
                }),
            )
            .subscribe();
    }

    /** Current page number for list requests (1-based). */
    nextPage$ = new BehaviorSubject(1);

    /** Main filter object for notifications list (merged into backend filter). */
    filter$ = new BehaviorSubject({});

    /** Selected tab id (example: `all`, `important`). */
    tab$ = new BehaviorSubject('all');

    /**
     * Local list of new notifications received from websocket (latest first).
     * Used to prepend socket items to unread list and current list.
     */
    protected _newNotifications = new TemplateObservable([]);

    /**
     * Stream of notifications coming from websocket.
     *
     * Filters notice types:
     * - `notice.message`
     * - `notice.system`
     * - `notice.toast`
     *
     * Normalizes snapshot fields:
     * - sets state to `sentState`
     * - sets `is_important` to false
     *
     * Side effects:
     * - prepends notification to `_newNotifications`
     * - triggers tabs count refresh and list refresh
     * - updates local storage map
     * - for `notice.toast`, shows a toast using `BazisToastService`
     */
    notificationsFromSocket$ = this.socketService.message$.pipe(
        filter(
            (v) =>
                v &&
                (v?.$snapshot?.notice_type === 'notice.message' ||
                    v?.$snapshot?.notice_type === 'notice.system' ||
                    v?.$snapshot?.notice_type === 'notice.toast'),
        ),
        map((v) => {
            return {
                ...v,
                $snapshot: {
                    ...v.$snapshot,
                    state: this.sentState,
                    is_important: false,
                },
            };
        }),
        tap((v) => {
            const newNotifications = this._newNotifications._;
            newNotifications.unshift(v);
            this._newNotifications.set(newNotifications);
            this._updateTabsCount$.next(true);
            this.nextPage$.next(this.nextPage$.value);
            this._updateStorage([v]);
            if (
                v?.$snapshot?.notice_type === 'notice.toast' &&
                (v?.$snapshot?.title || v?.$snapshot?.body)
            ) {
                const stateMap = {
                    success: 'success',
                    negative: 'error',
                    danger: 'error',
                    info: 'warning',
                };
                this.toastService.create({
                    type: stateMap[v?.$snapshot?.notice_level],
                    title: v?.$snapshot?.title,
                    message: v?.$snapshot?.body,
                });
            }
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Local notification storage map by id.
     * Used to “actualize” items in lists when data is updated.
     */
    protected _notificationStorage = new TemplateObservable({});

    /**
     * Emits whenever filter changes and resets pagination to first page.
     */
    protected _filterChanged$ = this.filter$.pipe(
        tap((v) => {
            this.nextPage$.next(1);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Tab filters stream derived from `tab$`.
     *
     * @returns Observable of tab-specific filter params object or `null`.
     */
    protected _tabFilters$ = this.tab$.pipe(
        map((tab) => {
            return TAB_PARAMS[tab] || null;
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Tabs list with dynamic counts.
     *
     * On filter or `_updateTabsCount$` trigger, requests counts for each tab using
     * `entityService.getListsCount$()` and fills `titleParams.count`.
     *
     * @returns Observable of tabs definitions array.
     */
    tabList$ = merge(this._filterChanged$, this._updateTabsCount$).pipe(
        debounceTime(0),
        withLatestFrom(this._filterChanged$),
        switchMap(([v, filters]) =>
            this.entityService.getListsCount$(
                ALL_TABS.map((tab) => ({
                    entityType: 'notifications.notice',
                    filters: {
                        ...filters,
                        ...TAB_PARAMS[tab.id],
                        [this.channelField]: 'websocket',
                    },
                })),
            ),
        ),
        map((response) => {
            let i = 0;
            return [...ALL_TABS].map((tab) => {
                if (!TAB_PARAMS[tab.id]) return tab;
                return {
                    ...tab,
                    titleParams: { count: response[i++] },
                };
            });
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    // list of notifications
    /**
     * Backend-driven notifications list stream for current page + filters + tab.
     *
     * Side effects:
     * - sets `isLoading` to true before request
     * - updates `pagination` from response meta
     * - updates local storage map with received list items
     *
     * @returns Observable of notifications list (array of `EntData`).
     */
    protected _listFromRequest$: Observable<EntData[]> = combineLatest([
        this.nextPage$,
        this._filterChanged$,
        this._tabFilters$,
    ]).pipe(
        debounceTime(0),
        tap((v) => this.isLoading.set(true)),
        switchMap(([page, filters, tabFilters]) =>
            this.entityService.getEntityList$('notifications.notice', {
                params: {
                    filter: buildFilterStr({
                        ...filters,
                        ...tabFilters,
                        [this.channelField]: 'websocket',
                    }),
                },
                offset: (page - 1) * this.pagination._.limit,
                limit: this.pagination._.limit,
            }),
        ),
        tap((list) => {
            this.pagination.set(list.$meta?.pagination);
            this._updateStorage(list.list);
        }),
        map((v) => v.list),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Actual notifications list stream exposed to UI.
     *
     * Combines backend list with local storage map and replaces items by latest stored versions.
     *
     * @returns Observable of notifications list.
     */
    notifications$ = combineLatest([this._listFromRequest$, this._notificationStorage.$]).pipe(
        debounceTime(10),
        map(([notifications, storage]) => this._actualize(notifications)),
        tap((v) => {
            this.isLoading.set(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    // list of unread notifications
    /**
     * Unread notifications list loaded from backend for websocket channel/provider.
     *
     * Disabled when current role is in `disableNotificationsRoles` list.
     *
     * Side effects:
     * - updates local storage map with received items
     *
     * @returns Observable of unread notifications list (`EntData[]`) or `null` when disabled.
     */
    protected unreadNotifications$: Observable<EntData[]> = combineLatest([
        this._updateTopNotificationsList$,
        this.authService.role$,
    ]).pipe(
        filter(([update, role]) => !!role),
        switchMap(([update, role]) =>
            this.disableNotificationsRoles.indexOf(role) === -1
                ? this.entityService.getEntityList$('notifications.notice', {
                      params: {
                          filter: buildFilterStr({
                              state: this.sentState,
                              [this.channelField]: 'websocket',
                          }),
                      },
                      limit: 1000,
                  })
                : of(null),
        ),
        tap((list) => {
            if (!list) return;
            this._updateStorage(list.list);
        }),
        map((v) => (v ? v.list : null)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Top notifications list (unread + new socket items).
     *
     * Merges unread list with `_newNotifications` and filters out:
     * - items with state `read`
     * - items with `is_skip_save`
     *
     * @returns Observable of notifications list or `null`.
     */
    topNotifications$ = combineLatest([
        this.unreadNotifications$,
        this._newNotifications.$,
        this._notificationStorage.$,
    ]).pipe(
        debounceTime(10),
        map(([unreadNotifications, newNotifications, storageData]) =>
            unreadNotifications
                ? this._mergeListItems(unreadNotifications, newNotifications)
                : null,
        ),
        map((notifications) =>
            notifications
                ? this._actualize(notifications).filter(
                      (v) => v.$snapshot.state !== 'read' && !v.$snapshot.is_skip_save,
                  )
                : null,
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Subset of `topNotifications$` that are blocking notices.
     *
     * @returns Observable of blocking notifications list or `null`.
     */
    blockingNotifications$ = this.topNotifications$.pipe(
        map((notifications) =>
            notifications ? notifications.filter((v) => v.$snapshot.is_blocking_notice) : null,
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Merge items into the beginning of main list, without duplicates by id.
     *
     * @param main - Base list.
     * @param addToBeginElements - Items to prepend (default: empty array).
     * @returns Updated main list reference.
     */
    protected _mergeListItems(main: any[], addToBeginElements: any[] = []) {
        const mainList = main;
        addToBeginElements.forEach((element) => {
            if (!mainList.find((v) => v.id === element.id)) {
                mainList.unshift(element);
            }
        });
        return main;
    }

    /**
     * Change notifications state for multiple ids (default state is `read`).
     *
     * Performs backend request `user/change_state_notifications` and updates local storage map.
     *
     * @param ids - List of notification ids.
     * @param state - Target state string. Default: `'read'`.
     * @returns void
     */
    changeState(ids: string[], state = 'read') {
        this.srv
            .sendFormRequest$('user/change_state_notifications', { state, notifications_id: ids })
            .pipe(
                tap(() => {
                    this._updateStoragePropery(ids, { state });
                    if (state !== 'read') {
                        this._updateTopNotificationsList$.next(true);
                    }
                }),
                take(1),
            )
            .subscribe();
    }

    /**
     * Toggle notification importance flag.
     *
     * Saves `is_important` to backend and updates local storage map and tabs count.
     * If current tab is `important`, re-triggers tab stream.
     *
     * @param id - Notification id string.
     * @param importance - New importance value.
     * @returns void
     */
    changeImportance(id: string, importance: boolean) {
        this.srv
            .saveEntity$(
                'notifications.notice',
                id,
                this.srv.generateEntityBody('notifications.notice', id, {
                    is_important: importance,
                }),
            )
            .pipe(
                tap(() => {
                    this._updateStoragePropery([id], { is_important: importance });
                    this._updateTabsCount$.next(true);
                }),
                withLatestFrom(this.tab$),
                tap(([response, tab]) => {
                    if (tab === 'important') this.tab$.next(tab);
                }),
                take(1),
            )
            .subscribe();
    }

    /**
     * Update one or more properties in local notification storage map for given ids.
     *
     * @param ids - Notification ids list.
     * @param propertyObject - Object with snapshot properties to merge.
     * @returns void
     */
    protected _updateStoragePropery(ids, propertyObject) {
        this._updateStorage(
            ids.map((id) => {
                return {
                    ...this._notificationStorage._[id],
                    $snapshot: {
                        ...this._notificationStorage._[id].$snapshot,
                        ...propertyObject,
                    },
                };
            }),
        );
    }

    /**
     * Merge notification items into local storage map by id.
     *
     * @param notifications - Array of notification entities.
     * @returns void
     */
    protected _updateStorage(notifications) {
        const newNotificationsMap = notifications.reduce((acc, notification) => {
            acc[notification.id] = notification;
            return acc;
        }, {});
        this._notificationStorage.set({ ...this._notificationStorage._, ...newNotificationsMap });
    }

    /**
     * Replace items in a list with their latest versions from local storage map.
     *
     * @param notifications - List of notification entities.
     * @returns List where each item is replaced by stored version when available.
     */
    protected _actualize(notifications) {
        return notifications.map((v) => {
            return this._notificationStorage._[v.id] || v;
        });
    }
}
